import type { IPropertyBag } from '@deity-io/di-data-basics';
import type { Flow, IComponentTypeConfig } from '@deity-io/di-data-flows';
import type { IValidationResult } from '@deity-io/di-data-flows-editor';
import type { IPoint } from 'contracts/interfaces';
import React from 'react';

export interface IFlowSchema {
  [key: string]: any;
  _meta: {
    [name: string]: any;
    type: string;
    trigger?: string;
  };
}

export type IPanels = 'ADD_COMPONENT' | 'JSON_VIEWER' | 'CONFIG_COMPONENT' | 'PORT_SCHEMA';

export enum FlowActionTypes {
  INIT_FLOW = 'INIT_FLOW',
  ADD_COMPONENT = 'ADD_COMPONENT',
  REMOVE_COMPONENT = 'REMOVE_COMPONENT',
  REMOVE_SELECTED = 'REMOVE_SELECTED',
  TRANSLATE_COMPONENT = 'TRANSLATE_COMPONENT',
  CONNECT = 'CONNECT',
  DISCONNECT = 'DISCONNECT',
  CONNECT_TRIGGER = 'CONNECT_TRIGGER',
  CONNECT_TAIL = 'CONNECT_TAIL',
  UPDATE_FLOW_SETTING = 'UPDATE_FLOW_SETTING',
  UPDATE_COMPONENT = 'UPDATE_COMPONENT',
  SELECT_COMPONENT = 'SELECT_COMPONENT',
  TOGGLE_PANEL = 'TOGGLE_PANEL',
  CLEAR = 'CLEAR',
  CLEAR_ERROR = 'CLEAR_ERROR',
  CHANGE_GROUP = 'CHANGE_GROUP',
  TOGGLE_COLLAPSED = 'TOGGLE_COLLAPSED',
  CREATE_FLOW_PORT = 'CREATE_FLOW_PORT',
  UPDATE_CONNECTION_WAYPOINTS = 'UPDATE_CONNECTION_WAYPOINTS'
}

export interface IInitFlow {
  type: FlowActionTypes.INIT_FLOW;
  payload: {
    uuid: string;
    schema: IFlowSchema;
    models: Array<IPropertyBag & { name: string }> | IPropertyBag;
    endpoints: Array<IPropertyBag & { name: string }> | IPropertyBag;
    mappings: Array<IPropertyBag & { name: string }> | IPropertyBag;
    flows: Array<IPropertyBag & { name: string }> | IPropertyBag;
    locked?: boolean;
  };
}

export type ISelectedComponent =
  | { componentId: string; flowId?: string }
  | { componentId: string; flowId?: string; port: string }
  | { componentId: string; flowId?: string; port: string; targetComponentId: string; targetPort: string }
  | { flowId?: string; flowPort: string; configPath?: Array<string> }
  | { flowId?: string; trigger: string; configPath?: Array<string> };

export interface ISelectComponent {
  type: FlowActionTypes.SELECT_COMPONENT;
  payload: null | ISelectedComponent;
}

export interface IClearState {
  type: FlowActionTypes.CLEAR;
}
export interface IClearErrorState {
  type: FlowActionTypes.CLEAR_ERROR;
}

export interface IUpdateComponent {
  type: FlowActionTypes.UPDATE_COMPONENT;
  payload: { compId: string; key: Array<string>; val: any; regenerateHash?: boolean };
}

export interface IEdgeNode {
  compId: string;
  portKey: string;
}
export interface IConnectComponentPayload {
  from: IEdgeNode;
  to: IEdgeNode;
}

export interface IConnectTriggerPayload {
  to: IEdgeNode;
}

export interface IConnectTailPayload {
  from: IEdgeNode;
  tail: string;
}

export interface IConnectPayload {
  from: IEdgeNode;
  to: IEdgeNode;
}

export interface IConnect {
  type: FlowActionTypes.CONNECT;
  payload: IConnectPayload;
}

export interface IDisconnect {
  type: FlowActionTypes.DISCONNECT;
  payload: IConnectPayload;
}

export interface IConnectTrigger {
  type: FlowActionTypes.CONNECT_TRIGGER;
  payload: IConnectTriggerPayload;
}

export interface IConnectTail {
  type: FlowActionTypes.CONNECT_TAIL;
  payload: IConnectTailPayload;
}

export interface ICreateFlowPort {
  type: FlowActionTypes.CREATE_FLOW_PORT;
  payload: {
    key: string;
    settings?: IPropertyBag;
    position: IPoint;
  };
}

export interface IUpdateFlowSetting {
  type: FlowActionTypes.UPDATE_FLOW_SETTING;
  payload: { property: string | Array<string>; value: string | undefined };
}

export interface IMovePayload {
  compId: string;
  point: { x: number; y: number };
  size?: { height: number; width: number };
  flowSettingPath?: Array<string>;
}
export interface ITranslate {
  type: FlowActionTypes.TRANSLATE_COMPONENT;
  payload: IMovePayload;
}

export interface ITogglePanel {
  type: FlowActionTypes.TOGGLE_PANEL;
  payload: IPanels | null;
}

export interface IAddComponentPayload {
  componentId: string;
  componentConfig: IComponentTypeConfig<string>;
  groupComponentId?: string;
}

export interface IAddComponent {
  type: FlowActionTypes.ADD_COMPONENT;
  payload: IAddComponentPayload;
}
export interface IRemoveComponent {
  type: FlowActionTypes.REMOVE_COMPONENT;
  payload: string;
}

export interface IRemoveSelected {
  type: FlowActionTypes.REMOVE_SELECTED;
}

export interface IChangeComponentGroup {
  type: FlowActionTypes.CHANGE_GROUP;
  payload: {
    componentId: string;
    targetGroupId?: string;
  };
}

export interface IToggleCollapse {
  type: FlowActionTypes.TOGGLE_COLLAPSED;
  payload: {
    componentId: string;
    collapsed: boolean;
  };
}
export interface IFlowSummary {
  requiredFields: number;
  unfilledRequiredFieldsCount: number;
  errors: { [key: string]: { requiredFields: string[] } };
}
export interface IUpdateConnectionWaypoints {
  type: FlowActionTypes.UPDATE_CONNECTION_WAYPOINTS;
  payload:
    | {
        componentId: string;
        port: string;
        waypoints: Array<IPoint>;
      }
    | {
        flowSettingPath: Array<string>;
        port: string;
        waypoints: Array<IPoint>;
      };
}

export type FlowActions =
  | IInitFlow
  | IAddComponent
  | IConnect
  | IDisconnect
  | IConnectTrigger
  | IConnectTail
  | ITranslate
  | IRemoveComponent
  | IRemoveSelected
  | ISelectComponent
  | IUpdateComponent
  | ITogglePanel
  | IUpdateFlowSetting
  | IClearState
  | IChangeComponentGroup
  | IToggleCollapse
  | ICreateFlowPort
  | IUpdateConnectionWaypoints
  | IClearErrorState;

export interface IFlowState {
  uuid?: string;
  activePanel: IPanels | null;
  selectedComponent: ISelectedComponent | null;
  flowSummary?: IValidationResult;
  flowSchema: IFlowSchema | null;
  flow: Flow | null;
  locked?: boolean;
  structureHash: string;
  reducerErrors?: Array<string>;
}
export interface FlowContextInterface {
  state: IFlowState;
  dispatch: (FlowAction: FlowActions) => void;
}
export const FlowContext = React.createContext<FlowContextInterface | null>(null);
